import { Permission } from '@affine/graphql';
import {
  generateFractionalIndexingKeyBetween,
  LiveData,
  Store,
} from '@toeverything/infra';
import {
  combineLatest,
  map,
  type Observable,
  of,
  Subject,
  switchMap,
} from 'rxjs';

import { WorkspaceDBService } from '../../db';
import type { WorkspacePermissionService } from '../../permissions';
import type { WorkspaceService } from '../../workspace';
import { createCachedFolderAreaResolver } from '../utils/folder-area';
import type {
  AccessibleFolderCacheStore,
  AccessibleFolderMeta,
} from './accessible-folder-cache';

export type DocLinkEvent =
  | { type: 'linked'; folderId: string; docId: string }
  | { type: 'unlinked'; folderId: string; docId: string }
  | {
      type: 'moved';
      oldFolderId: string;
      newFolderId: string;
      docId: string;
    };

/** sl-sffq: Events emitted when folders are trashed/restored in Y.js. */
export type FolderTrashEvent =
  | { type: 'trashed'; folderId: string }
  | { type: 'restored'; folderId: string };

/** Row shape returned by watchNodeChildren / watchNodeInfo. */
type FolderRow = {
  data: string;
  type: (string & {}) | 'folder' | 'doc';
  index: string;
  id: string;
  parentId?: string | null;
  trashed?: boolean | null;
};

export interface DocFolderBreadcrumb {
  id: string;
  name: string;
}

export interface DocFolderBreadcrumbPath {
  area: 'apps' | 'personal' | 'teamAreas';
  folders: DocFolderBreadcrumb[];
  publicArea: boolean;
}

const DEFAULT_DOC_FOLDER_BREADCRUMB_PATH: DocFolderBreadcrumbPath = {
  area: 'teamAreas',
  folders: [],
  publicArea: true,
};

const CACHE_DOC_LINK_PREFIX = 'cache-doc-link-';

function assignCacheIndexes(rows: FolderRow[]): FolderRow[] {
  if (rows.length === 0) {
    return rows;
  }
  let previousIndex: string | null = null;
  return rows.map(row => {
    const index = generateFractionalIndexingKeyBetween(previousIndex, null);
    previousIndex = index;
    return {
      ...row,
      index,
    };
  });
}

function createCacheDocLinkId(parentId: string, docId: string): string {
  return `${CACHE_DOC_LINK_PREFIX}${parentId.length}:${parentId}:${docId}`;
}

function parseCacheDocLinkId(
  nodeId: string,
  folders: Array<{ id: string }>
): { parentId: string; docId: string } | null {
  if (!nodeId.startsWith(CACHE_DOC_LINK_PREFIX)) {
    return null;
  }

  const rest = nodeId.slice(CACHE_DOC_LINK_PREFIX.length);
  const lengthSeparator = rest.indexOf(':');
  if (lengthSeparator > 0) {
    const parentIdLength = Number(rest.slice(0, lengthSeparator));
    const payload = rest.slice(lengthSeparator + 1);
    if (Number.isInteger(parentIdLength) && parentIdLength > 0) {
      const parentId = payload.slice(0, parentIdLength);
      const docSeparator = payload[parentIdLength];
      const docId = payload.slice(parentIdLength + 1);
      if (parentId && docSeparator === ':' && docId) {
        return { parentId, docId };
      }
    }
  }

  // Legacy cache IDs used `${parentId}-${docId}` and assumed UUID-length
  // folder IDs. Decode by matching the known folder ID prefix instead.
  const matchingFolder = folders
    .slice()
    .sort((a, b) => b.id.length - a.id.length)
    .find(folder => rest.startsWith(`${folder.id}-`));
  if (!matchingFolder) {
    return null;
  }

  const docId = rest.slice(matchingFolder.id.length + 1);
  return docId ? { parentId: matchingFolder.id, docId } : null;
}

function collectCacheWorkspaceRootIds(
  folders: AccessibleFolderMeta[]
): Set<string> {
  const rootIds = new Set<string>();

  for (const folder of folders) {
    if (folder.isWorkspaceRoot) {
      rootIds.add(folder.id);
    }
  }

  return rootIds;
}

/**
 * FolderStore — Dual-mode folder data access.
 *
 * sl-oiya.5: Security model for cloud workspaces:
 * - Owner/Admin: Mutate via Y.js `db$folders`, render from the server-derived
 *   AccessibleFolderCacheStore to avoid stale restricted-table flicker.
 * - Member/Reader/Editor: Read from AccessibleFolderCacheStore (GraphQL, server-filtered)
 *   Write via FolderApiService (GraphQL mutations)
 *
 * Y.js `db$folders` is denied to non-Owner/Admin by DocPermissionService to prevent
 * private folder metadata (names, hierarchy, doc assignments) from leaking via CRDT sync.
 *
 * Local workspaces: Always use Y.js (no server, no RBAC).
 */
export class FolderStore extends Store {
  readonly docLinkEvents$ = new Subject<DocLinkEvent>();
  /** sl-sffq: Emitted when a folder is trashed/restored in Y.js. */
  readonly folderTrashEvents$ = new Subject<FolderTrashEvent>();

  constructor(
    private readonly dbService: WorkspaceDBService,
    private readonly workspaceService: WorkspaceService,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore,
    private readonly permissionService: WorkspacePermissionService
  ) {
    super();
    this.disposables.push(() => this.docLinkEvents$.complete());
    this.disposables.push(() => this.folderTrashEvents$.complete());

    // sl-oiya.5: Connect db$folders to sync engine only for Owner/Admin.
    // For Members, db$folders stays unsynced (empty Y.js doc) — they use
    // the GraphQL cache path instead. This prevents CRDT metadata leaks.
    if (this.isCloudWorkspace) {
      let connected = false;
      const sub = this.permissionService.permission.isOwnerOrAdmin$.subscribe(
        isOwnerOrAdmin => {
          if (isOwnerOrAdmin && !connected) {
            connected = true;
            this.dbService.connectRestrictedTable('folders');
          }
        }
      );
      this.disposables.push(() => sub.unsubscribe());
    }
  }

  private get isCloudWorkspace(): boolean {
    return this.workspaceService.workspace.flavour !== 'local';
  }

  private assertCanMutateYjsFolders() {
    if (!this.isCloudWorkspace) {
      return;
    }
    const role = this.permissionService.permission.role$.value;
    if (role !== Permission.Owner && role !== Permission.Admin) {
      throw new Error(
        'Cloud folder mutations for non-admin members must use FolderApiService'
      );
    }
  }

  /**
   * Whether the current user can access Y.js db$folders directly.
   * Owner/Admin sync the full Y.js doc; others use GraphQL cache.
   */
  private get canSyncYjsFolders$(): Observable<boolean> {
    if (!this.isCloudWorkspace) {
      return of(true);
    }
    return this.permissionService.permission.isOwnerOrAdmin$.pipe(
      map(v => v === true)
    );
  }

  watchNodeInfo(nodeId: string): Observable<FolderRow | null> {
    if (!this.isCloudWorkspace) {
      return this.dbService.db.folders.get$(nodeId);
    }

    return this.canSyncYjsFolders$.pipe(
      switchMap(canSync => {
        if (canSync) {
          return combineLatest([
            this.dbService.db.folders.get$(nodeId),
            this.accessibleFolderCache.watchAllAccessibleFolders(),
            this.accessibleFolderCache.isPopulated$,
          ]).pipe(
            map(([_row, folders, cachePopulated]) =>
              cachePopulated
                ? this.createNodeInfoFromCache(nodeId, folders)
                : null
            )
          );
        }
        // Member path: synthesize from GraphQL cache
        return this.accessibleFolderCache
          .watchAllAccessibleFolders()
          .pipe(map(folders => this.createNodeInfoFromCache(nodeId, folders)));
      })
    );
  }

  private createNodeInfoFromCache(
    nodeId: string,
    folders: AccessibleFolderMeta[]
  ): FolderRow | null {
    const cached = folders.find(f => f.id === nodeId);
    if (cached) {
      return (
        assignCacheIndexes(
          folders
            .filter(folder => folder.parentId === cached.parentId)
            .map(folder => ({
              id: folder.id,
              type: 'folder' as const,
              data: folder.name,
              index: '',
              parentId: folder.parentId,
            }))
        ).find(folder => folder.id === nodeId) ?? null
      );
    }

    const docLink = parseCacheDocLinkId(nodeId, folders);
    if (docLink) {
      return (
        this.createNodeChildrenFromCache(docLink.parentId, folders).find(
          child => child.id === nodeId
        ) ?? null
      );
    }

    return null;
  }

  /**
   * Watch children of a folder node.
   *
   * sl-oiya.5 Dual-mode:
   * - Owner/Admin (cloud): AccessibleFolderCacheStore for render authority;
   *   Y.js db$folders remains the local mutation source.
   * - Member (cloud): AccessibleFolderCacheStore (server-filtered, no Y.js sync)
   * - Local: Y.js db$folders (no RBAC)
   */
  watchNodeChildren(parentId: string | null): Observable<FolderRow[]> {
    if (!this.isCloudWorkspace) {
      return this.dbService.db.folders
        .find$({ parentId })
        .pipe(map(rows => rows.filter(r => !r.trashed)));
    }

    return this.canSyncYjsFolders$.pipe(
      switchMap(canSync => {
        if (canSync) {
          return combineLatest([
            this.dbService.db.folders
              .find$({ parentId })
              .pipe(map(rows => rows.filter(r => !r.trashed))),
            this.accessibleFolderCache.watchAllAccessibleFolders(),
            this.accessibleFolderCache.isPopulated$,
          ]).pipe(
            map(([_rows, folders, cachePopulated]) => {
              if (cachePopulated) {
                return this.createNodeChildrenFromCache(parentId, folders);
              }
              return [];
            })
          );
        }
        // Member: synthesize folder tree from server-filtered GraphQL cache
        return this.watchNodeChildrenFromCache(parentId);
      })
    );
  }

  watchDocFolderBreadcrumbPath(
    docId: string
  ): Observable<DocFolderBreadcrumbPath> {
    if (!this.isCloudWorkspace) {
      return this.dbService.db.folders.find$().pipe(
        map(rows => ({
          ...DEFAULT_DOC_FOLDER_BREADCRUMB_PATH,
          folders: this.resolveYjsDocFolderBreadcrumbs(rows, docId),
        }))
      );
    }

    return this.accessibleFolderCache
      .watchAllAccessibleFolders()
      .pipe(
        map(folders => this.resolveCachedDocFolderBreadcrumbs(folders, docId))
      );
  }

  private resolveYjsDocFolderBreadcrumbs(
    rows: FolderRow[],
    docId: string
  ): DocFolderBreadcrumb[] {
    const docLink = rows.find(
      row => row.type === 'doc' && row.data === docId && !row.trashed
    );
    if (!docLink?.parentId) {
      return [];
    }

    const foldersById = new Map(
      rows
        .filter(row => row.type === 'folder' && !row.trashed)
        .map(row => [row.id, row])
    );
    const breadcrumbs: DocFolderBreadcrumb[] = [];
    const visited = new Set<string>();
    let folderId: string | null | undefined = docLink.parentId;

    while (folderId && !visited.has(folderId)) {
      visited.add(folderId);
      const folder = foldersById.get(folderId);
      if (!folder) break;
      breadcrumbs.unshift({ id: folder.id, name: folder.data });
      folderId = folder.parentId;
    }

    return breadcrumbs;
  }

  private resolveCachedDocFolderBreadcrumbs(
    folders: AccessibleFolderMeta[],
    docId: string
  ): DocFolderBreadcrumbPath {
    const linkedFolder = folders.find(folder => folder.docIds.includes(docId));
    if (!linkedFolder) {
      return DEFAULT_DOC_FOLDER_BREADCRUMB_PATH;
    }

    const foldersById = new Map(folders.map(folder => [folder.id, folder]));
    const resolveArea = createCachedFolderAreaResolver(folders);
    const resolvedArea = resolveArea(linkedFolder);
    const breadcrumbs: DocFolderBreadcrumb[] = [];
    const visited = new Set<string>();
    let folder: AccessibleFolderMeta | undefined = linkedFolder;

    while (folder && !visited.has(folder.id)) {
      visited.add(folder.id);
      if (
        !folder.isWorkspaceRoot &&
        folder.systemKind !== 'private_area_roots' &&
        folder.systemKind !== 'app_area_root'
      ) {
        breadcrumbs.unshift({ id: folder.id, name: folder.name });
      }
      folder = folder.parentId ? foldersById.get(folder.parentId) : undefined;
    }

    return {
      area:
        resolvedArea === 'private'
          ? 'personal'
          : resolvedArea === 'app'
            ? 'apps'
            : 'teamAreas',
      folders: breadcrumbs,
      publicArea: resolvedArea === 'public',
    };
  }

  /**
   * Synthesize watchNodeChildren from AccessibleFolderCacheStore for Members.
   * The cache contains ONLY folders the user has permission to see (server-filtered).
   *
   * IMPORTANT hierarchy mapping:
   * - PostgreSQL: sub-folders have parentId = rootFolderId ("General")
   * - Y.js / UI: user-created folders have parentId = null (virtual root)
   * - The Organize sidebar filters out the workspace root folder and expects
   *   its children to appear at root level (parentId = null).
   * - So for parentId === null, we return BOTH direct null-parent folders AND
   *   children of the workspace root folder (promoted to root level).
   */
  private watchNodeChildrenFromCache(
    parentId: string | null
  ): Observable<FolderRow[]> {
    return this.accessibleFolderCache
      .watchAllAccessibleFolders()
      .pipe(
        map(folders => this.createNodeChildrenFromCache(parentId, folders))
      );
  }

  private createNodeChildrenFromCache(
    parentId: string | null,
    folders: AccessibleFolderMeta[]
  ): FolderRow[] {
    const result: FolderRow[] = [];
    const workspaceRootIds = collectCacheWorkspaceRootIds(folders);
    const resolveArea = createCachedFolderAreaResolver(folders);
    const parentFolder =
      parentId === null
        ? null
        : (folders.find(folder => folder.id === parentId) ?? null);
    const parentArea =
      parentId === null
        ? 'public'
        : parentFolder
          ? resolveArea(parentFolder)
          : null;

    for (const folder of folders) {
      if (workspaceRootIds.has(folder.id)) continue;
      const isDirectChild = folder.parentId === parentId;
      const isPromotedChild =
        parentId === null &&
        folder.parentId !== null &&
        workspaceRootIds.has(folder.parentId);
      if (!isDirectChild && !isPromotedChild) {
        continue;
      }

      // Root-level FolderStore consumers render the Public Area only; the
      // dedicated Private/App/Dynamic Area roots own their own rows. Below an
      // area root, however, descendants must stay in that parent's canonical
      // area. A hard-coded Public filter made nested Folders disappear after
      // moving a filled tree into a Dynamic Area while its Documents remained.
      const expectedArea = isPromotedChild ? 'public' : parentArea;
      if (expectedArea === null || resolveArea(folder) !== expectedArea) {
        continue;
      }

      result.push({
        id: folder.id,
        type: 'folder' as const,
        data: folder.name,
        index: '',
        parentId: parentId,
      });
    }

    // Add doc-links for non-root folder children
    if (parentId !== null) {
      const parentFolder = folders.find(f => f.id === parentId);
      if (parentFolder && !parentFolder.metadataOnly && parentFolder.docIds) {
        for (const docId of parentFolder.docIds) {
          result.push({
            id: createCacheDocLinkId(parentId, docId),
            type: 'doc' as const,
            data: docId,
            index: '',
            parentId,
          });
        }
      }
    }

    return assignCacheIndexes(result);
  }

  watchIsLoading(): LiveData<boolean> {
    if (!this.isCloudWorkspace) {
      return this.dbService.db.folders.isLoading$;
    }

    // Cloud navigation renders from the server-derived folder cache for every
    // role. Owner/Admin may still sync Y.js for writes, but loading must not
    // unblock on a stale restricted-table snapshot.
    return LiveData.from(
      this.canSyncYjsFolders$.pipe(
        switchMap(canSync => {
          if (canSync) {
            return this.accessibleFolderCache.isPopulated$.pipe(
              map(populated => !populated)
            );
          }
          // Member: loading until cache is populated
          return this.accessibleFolderCache.isPopulated$.pipe(
            map(populated => !populated)
          );
        })
      ),
      true // Start as loading
    );
  }

  isAncestor(childId: string, ancestorId: string): boolean {
    if (childId === ancestorId) {
      return false;
    }
    const history = new Set<string>([childId]);
    let current: string = childId;
    while (current) {
      const info = this.dbService.db.folders.get(current);
      if (info === null || !info.parentId) {
        return false;
      }
      current = info.parentId;
      if (history.has(current)) {
        return false; // loop detected
      }
      history.add(current);
      if (current === ancestorId) {
        return true;
      }
    }
    return false;
  }

  createLink(parentId: string, type: 'doc', nodeId: string, index: string) {
    this.assertCanMutateYjsFolders();
    const parent = this.dbService.db.folders.get(parentId);
    if (parent === null || parent.type !== 'folder') {
      throw new Error('Parent folder not found');
    }

    // Remove existing link for this item to prevent duplicates.
    // A doc/tag/collection can only exist in one folder at a time.
    const existingLink = this.findLinkByData(type, nodeId);
    if (existingLink) {
      if (existingLink.parentId === parentId) {
        return existingLink.id; // Already in this folder — no-op
      }
      const oldFolderId = existingLink.parentId;
      this.dbService.db.folders.delete(existingLink.id);

      if (type === 'doc' && oldFolderId) {
        // Emit 'moved' instead of separate unlink+link
        const link = this.dbService.db.folders.create({
          parentId,
          type,
          data: nodeId,
          index,
        });
        this.docLinkEvents$.next({
          type: 'moved',
          oldFolderId,
          newFolderId: parentId,
          docId: nodeId,
        });
        return link.id;
      }
    }

    const link = this.dbService.db.folders.create({
      parentId,
      type,
      data: nodeId,
      index,
    });

    if (type === 'doc') {
      this.docLinkEvents$.next({
        type: 'linked',
        folderId: parentId,
        docId: nodeId,
      });
    }
    return link.id;
  }

  renameNode(nodeId: string, name: string) {
    this.assertCanMutateYjsFolders();
    const node = this.dbService.db.folders.get(nodeId);
    if (node === null) {
      throw new Error('Node not found');
    }
    if (node.type !== 'folder') {
      throw new Error('Cannot rename non-folder node');
    }
    this.dbService.db.folders.update(nodeId, {
      data: name,
    });
  }

  createFolder(
    parentId: string | null,
    name: string,
    index: string,
    id?: string
  ) {
    this.assertCanMutateYjsFolders();
    if (parentId) {
      if (!this.getYjsFolderNodeInfo(parentId)) {
        this.ensureFolderMirrorFromCache(parentId);
      }
      const parent = this.dbService.db.folders.get(parentId);
      if (parent === null || parent.type !== 'folder') {
        throw new Error('Parent folder not found');
      }
    }

    return this.dbService.db.folders.create({
      ...(id !== undefined && { id }),
      parentId: parentId,
      type: 'folder',
      data: name,
      index: index,
    }).id;
  }

  removeFolder(folderId: string) {
    this.assertCanMutateYjsFolders();
    const info = this.dbService.db.folders.get(folderId);
    if (info === null || info.type !== 'folder') {
      throw new Error('Folder not found');
    }
    const stack = [info];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) {
        continue;
      }
      if (current.type !== 'folder') {
        if (current.type === 'doc' && current.parentId) {
          this.docLinkEvents$.next({
            type: 'unlinked',
            folderId: current.parentId,
            docId: current.data,
          });
        }
        this.dbService.db.folders.delete(current.id);
      } else {
        const children = this.dbService.db.folders.find({
          parentId: current.id,
        });
        stack.push(...children);
        this.dbService.db.folders.delete(current.id);
      }
    }
  }

  /**
   * sl-sffq: Soft-delete a folder and all its descendants in Y.js (local-first).
   * Sets trashed=true on the folder, all sub-folders, and all doc-links.
   * DB sync is handled asynchronously by FolderDocSyncService.
   */
  trashFolder(folderId: string): string[] {
    this.assertCanMutateYjsFolders();
    const folder = this.dbService.db.folders.get(folderId);
    if (folder === null || folder.type !== 'folder') {
      throw new Error('Folder not found');
    }
    const trashedIds = this.collectDescendants(folderId);
    for (const id of trashedIds) {
      this.dbService.db.folders.update(id, { trashed: true });
    }
    this.folderTrashEvents$.next({ type: 'trashed', folderId });
    return trashedIds;
  }

  /**
   * sl-sffq: Restore a trashed folder and all its descendants in Y.js (local-first).
   * Sets trashed=false on the folder, all sub-folders, and all doc-links.
   * DB sync is handled asynchronously by FolderDocSyncService.
   */
  restoreFolder(folderId: string): string[] {
    this.assertCanMutateYjsFolders();
    const folder = this.dbService.db.folders.get(folderId);
    if (folder === null || folder.type !== 'folder') {
      throw new Error('Folder not found');
    }
    const restoredIds = this.collectDescendants(folderId);
    for (const id of restoredIds) {
      this.dbService.db.folders.update(id, { trashed: false });
    }
    this.folderTrashEvents$.next({ type: 'restored', folderId });
    return restoredIds;
  }

  /**
   * Trash a folder in the local Y.js mirror without emitting a server sync
   * event. Used when another server-authoritative mutation already performs
   * the backend trash and the sidebar must hide the folder immediately.
   */
  trashFolderSilent(folderId: string): void {
    const folder = this.dbService.db.folders.get(folderId);
    if (folder === null || folder.type !== 'folder') {
      return;
    }
    const descendantIds = this.collectDescendants(folderId);
    for (const id of descendantIds) {
      this.dbService.db.folders.update(id, { trashed: true });
    }
  }

  /**
   * sl-sffq: Collect a folder and all its descendants (folders + doc-links).
   * BFS traversal of the Y.js folder tree.
   */
  private collectDescendants(folderId: string): string[] {
    const result = [folderId];
    const stack = [folderId];
    while (stack.length > 0) {
      const currentId = stack.pop()!;
      const children = this.dbService.db.folders.find({
        parentId: currentId,
      });
      for (const child of children) {
        result.push(child.id);
        if (child.type === 'folder') {
          stack.push(child.id);
        }
      }
    }
    return result;
  }

  collectDescendantDocIds(folderId: string): string[] {
    const result = new Set<string>();
    const stack = [folderId];
    while (stack.length > 0) {
      const currentId = stack.pop()!;
      const children = this.dbService.db.folders.find({
        parentId: currentId,
      });
      for (const child of children) {
        if (child.type === 'folder') {
          stack.push(child.id);
        } else if (child.type === 'doc') {
          result.add(child.data);
        }
      }
    }
    return [...result];
  }

  getYjsFolderSubtreeSnapshot(
    folderId: string,
    options: {
      ownerUserId?: string | null;
      visibility?: 'public' | 'private';
    } = {}
  ): AccessibleFolderMeta[] {
    const root = this.dbService.db.folders.get(folderId);
    if (root === null || root.type !== 'folder') {
      return [];
    }

    const result: AccessibleFolderMeta[] = [];
    const stack = [root];
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current || current.type !== 'folder') {
        continue;
      }

      const children = this.dbService.db.folders.find({
        parentId: current.id,
      });
      result.push({
        id: current.id,
        name: current.data,
        parentId: current.parentId ?? null,
        ownerUserId: options.ownerUserId ?? null,
        systemKind: null,
        visibility: options.visibility ?? 'public',
        inheritPermissions: true,
        isWorkspaceRoot: false,
        immutable: false,
        storageLimitBytes: null,
        storageUsedBytes: null,
        metadataOnly: false,
        docIds: children
          .filter((child): child is typeof child & { type: 'doc' } => {
            return child.type === 'doc';
          })
          .map(child => child.data),
      });
      stack.push(...children.filter(child => child.type === 'folder'));
    }

    return result;
  }

  removeLink(linkId: string) {
    this.assertCanMutateYjsFolders();
    const link = this.dbService.db.folders.get(linkId);
    if (link === null || link.type === 'folder') {
      throw new Error('Link not found');
    }

    if (link.type === 'doc' && link.parentId) {
      this.docLinkEvents$.next({
        type: 'unlinked',
        folderId: link.parentId,
        docId: link.data,
      });
    }

    this.dbService.db.folders.delete(linkId);
  }

  // Remove only the local Y.js mirror after a server-authoritative move.
  removeLinkLocalOnly(linkId: string) {
    this.assertCanMutateYjsFolders();
    const link = this.dbService.db.folders.get(linkId);
    if (link === null || link.type === 'folder') {
      throw new Error('Link not found');
    }

    this.dbService.db.folders.delete(linkId);
  }

  removeDocLinkIfPresent(linkId: string): boolean {
    this.assertCanMutateYjsFolders();
    const link = this.dbService.db.folders.get(linkId);
    if (link === null || link.type !== 'doc') {
      return false;
    }

    if (link.parentId) {
      this.docLinkEvents$.next({
        type: 'unlinked',
        folderId: link.parentId,
        docId: link.data,
      });
    }

    this.dbService.db.folders.delete(linkId);
    return true;
  }

  removeDocLinksLocalOnly(docId: string): number {
    if (this.isCloudWorkspace) {
      const role = this.permissionService.permission.role$.value;
      if (role !== Permission.Owner && role !== Permission.Admin) {
        return 0;
      }
    }

    const linkIds: string[] = [];
    const stack = this.dbService.db.folders.find({ parentId: null });

    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) continue;

      if (current.type === 'doc' && current.data === docId) {
        linkIds.push(current.id);
        continue;
      }

      if (current.type === 'folder') {
        stack.push(...this.dbService.db.folders.find({ parentId: current.id }));
      }
    }

    for (const linkId of linkIds) {
      this.dbService.db.folders.delete(linkId);
    }

    return linkIds.length;
  }

  /**
   * Find an existing link node for a given item in the Y.js folder tree.
   * Returns the first match or null if not found.
   */
  private findLinkByData(
    type: 'doc',
    data: string
  ): { id: string; parentId: string | null } | null {
    const stack = this.dbService.db.folders.find({ parentId: null });
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) continue;
      if (current.type === type && current.data === data) {
        return { id: current.id, parentId: current.parentId ?? null };
      }
      if (current.type === 'folder') {
        stack.push(...this.dbService.db.folders.find({ parentId: current.id }));
      }
    }
    return null;
  }

  /**
   * Collect all doc-link entries from the Y.js folder tree.
   * Used by FolderDocSyncService.reconcile() to detect drift.
   */
  getAllDocLinks(): Array<{ folderId: string; docId: string }> {
    const result: Array<{ folderId: string; docId: string }> = [];
    const stack = this.dbService.db.folders.find({ parentId: null });
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) continue;
      if (current.type === 'doc' && current.parentId) {
        result.push({ folderId: current.parentId, docId: current.data });
      } else if (current.type === 'folder') {
        stack.push(...this.dbService.db.folders.find({ parentId: current.id }));
      }
    }
    return result;
  }

  /**
   * sl-sffq.2: Collect all folder nodes from the Y.js tree (including trashed).
   * Used by FolderDocSyncService.reconcileTrashedStatus() to compare with server.
   */
  getAllFolderNodes(): Array<{
    id: string;
    data: string;
    trashed?: boolean | null;
    parentId?: string | null;
  }> {
    const result: Array<{
      id: string;
      data: string;
      trashed?: boolean | null;
      parentId?: string | null;
    }> = [];
    const stack = this.dbService.db.folders.find({ parentId: null });
    while (stack.length > 0) {
      const current = stack.pop();
      if (!current) continue;
      if (current.type === 'folder') {
        result.push({
          id: current.id,
          data: current.data,
          trashed: current.trashed,
          parentId: current.parentId,
        });
        stack.push(...this.dbService.db.folders.find({ parentId: current.id }));
      }
    }
    return result;
  }

  getYjsFolderNodeInfo(folderId: string): {
    id: string;
    data: string;
    index: string;
    parentId?: string | null;
    trashed?: boolean | null;
  } | null {
    const node = this.dbService.db.folders.get(folderId);
    if (node === null || node.type !== 'folder') {
      return null;
    }

    return {
      id: node.id,
      data: node.data,
      index: node.index,
      parentId: node.parentId,
      trashed: node.trashed,
    };
  }

  getYjsDocLinkInfo(linkId: string): {
    id: string;
    data: string;
    index: string;
    parentId?: string | null;
  } | null {
    const node = this.dbService.db.folders.get(linkId);
    if (node === null || node.type !== 'doc') {
      return null;
    }

    return {
      id: node.id,
      data: node.data,
      index: node.index,
      parentId: node.parentId,
    };
  }

  private ensureFolderMirrorFromCache(
    folderId: string,
    visited = new Set<string>()
  ): boolean {
    const existing = this.getYjsFolderNodeInfo(folderId);
    if (existing) {
      return true;
    }
    if (visited.has(folderId)) {
      return false;
    }
    visited.add(folderId);

    const cached = this.accessibleFolderCache.getCachedFolder(folderId);
    if (!cached || cached.isWorkspaceRoot || cached.systemKind !== null) {
      return false;
    }

    let parentId: string | null = null;
    if (cached.parentId) {
      const cachedParent = this.accessibleFolderCache.getCachedFolder(
        cached.parentId
      );
      const parentIsVirtualRoot =
        !cachedParent ||
        cachedParent.isWorkspaceRoot ||
        cachedParent.systemKind === 'private_area_roots' ||
        cachedParent.systemKind === 'private_root' ||
        cachedParent.systemKind === 'private_mail';
      if (!parentIsVirtualRoot) {
        parentId = this.ensureFolderMirrorFromCache(cached.parentId, visited)
          ? cached.parentId
          : null;
      }
    }

    this.dbService.db.folders.create({
      id: cached.id,
      parentId,
      type: 'folder',
      data: cached.name,
      index: generateFractionalIndexingKeyBetween(null, null),
    });
    return true;
  }

  moveDocLinkOrCreate(
    parentId: string,
    docId: string,
    index: string,
    sourceLinkId?: string | null
  ): string {
    this.assertCanMutateYjsFolders();
    if (!this.getYjsFolderNodeInfo(parentId)) {
      this.ensureFolderMirrorFromCache(parentId);
    }

    const parent = this.dbService.db.folders.get(parentId);
    if (parent === null || parent.type !== 'folder') {
      throw new Error('Parent folder not found');
    }

    const sourceLink = sourceLinkId
      ? this.getYjsDocLinkInfo(sourceLinkId)
      : null;
    if (sourceLink?.data === docId) {
      this.moveNode(sourceLink.id, parentId, index);
      return sourceLink.id;
    }

    return this.createLink(parentId, 'doc', docId, index);
  }

  /**
   * sl-sffq.2: Restore a folder without emitting folderTrashEvents$.
   * Used by reconciliation to sync server state to Y.js without triggering
   * a redundant server round-trip.
   */
  restoreFolderSilent(folderId: string): void {
    const folder = this.dbService.db.folders.get(folderId);
    if (folder === null || folder.type !== 'folder') {
      return; // Silently skip — reconciliation should not throw
    }
    const descendantIds = this.collectDescendants(folderId);
    for (const id of descendantIds) {
      this.dbService.db.folders.update(id, { trashed: false });
    }
  }

  /**
   * sl-sffq: Reactive observable of trashed folder nodes from Y.js.
   * Updates automatically when Y.js receives changes (local or remote sync).
   * Used by the Trash Page for real-time updates.
   */
  watchTrashedFolders$(): Observable<
    Array<{
      id: string;
      name: string;
      parentId: string | null;
      docIds: string[];
    }>
  > {
    // Watch ALL folder-type nodes, filter trashed reactively
    return this.dbService.db.folders.find$({ type: 'folder' }).pipe(
      map(folders => {
        const result: Array<{
          id: string;
          name: string;
          parentId: string | null;
          docIds: string[];
        }> = [];

        for (const folder of folders) {
          if (!folder.trashed) continue;
          const children = this.dbService.db.folders.find({
            parentId: folder.id,
          });
          const docIds = children
            .filter((c: { type: string }) => c.type === 'doc')
            .map((c: { data: string }) => c.data);
          result.push({
            id: folder.id,
            name: folder.data,
            parentId: folder.parentId ?? null,
            docIds,
          });
        }

        return result;
      })
    );
  }

  /**
   * sl-sffq: Get all trashed folder nodes from Y.js.
   * Used by the Trash Page to show locally-trashed folders (offline-capable).
   * Returns folder-type nodes where trashed=true, with their doc-link children.
   */
  getTrashedFolderNodes(): Array<{
    id: string;
    name: string;
    parentId: string | null;
    docIds: string[];
  }> {
    const result: Array<{
      id: string;
      name: string;
      parentId: string | null;
      docIds: string[];
    }> = [];

    // Find all trashed folder-type nodes
    const allNodes = this.dbService.db.folders.find({ type: 'folder' });
    for (const node of allNodes) {
      if (!node.trashed) continue;
      // Collect doc-link children of this folder
      const children = this.dbService.db.folders.find({ parentId: node.id });
      const docIds = children
        .filter((c: { type: string; data: string }) => c.type === 'doc')
        .map((c: { data: string }) => c.data);

      result.push({
        id: node.id,
        name: node.data,
        parentId: node.parentId ?? null,
        docIds,
      });
    }

    return result;
  }

  moveNode(nodeId: string, parentId: string | null, index: string) {
    this.assertCanMutateYjsFolders();
    const node = this.dbService.db.folders.get(nodeId);
    if (node === null) {
      throw new Error('Node not found');
    }

    // sl-sffq: Prevent moving trashed nodes or moving into trashed parents
    if (node.trashed) {
      throw new Error('Cannot move a trashed node');
    }

    if (parentId) {
      if (nodeId === parentId) {
        throw new Error('Cannot move a node to itself');
      }
      if (this.isAncestor(parentId, nodeId)) {
        throw new Error('Cannot move a node to its descendant');
      }
      if (!this.getYjsFolderNodeInfo(parentId)) {
        this.ensureFolderMirrorFromCache(parentId);
      }
      const parent = this.dbService.db.folders.get(parentId);
      if (parent === null || parent.type !== 'folder') {
        throw new Error('Parent folder not found');
      }
      if (parent.trashed) {
        throw new Error('Cannot move a node into a trashed folder');
      }
    } else {
      if (node.type !== 'folder') {
        throw new Error('Root node can only have folders');
      }
    }

    const oldParentId = node.parentId;

    this.dbService.db.folders.update(nodeId, {
      parentId,
      index,
    });

    if (node.type === 'doc' && parentId && oldParentId !== parentId) {
      this.docLinkEvents$.next({
        type: 'moved',
        oldFolderId: oldParentId ?? '',
        newFolderId: parentId,
        docId: node.data,
      });
    }
  }
}
